import {BaseFactory} from "../../../infrastructure/factories/base-factory";
import {contractsUtils} from "../../../infrastructure/utils/contracts-utils";
import {SportDomainModel} from "../../sports/models/sport-domain-model";
import {MemberFactory} from "../member/member-factory";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {LineupsApiModel} from "./lineups-api-model";
import {MemberApiModel} from "../member/member-api-model";
import {LineupsDomainModel} from "./lineups-domain-model";
import {MemberDomainModel} from "../member/member-domain-model";
import {LineupsMemberApiModel} from "./lineups-member/lineups-member-api-model";
import {LineupsMemberDomainModel} from "./lineups-member/lineups-member-domain-model";
import {RankingsApiModel} from "../rankings/rankings.api-model";
import {RankingsDomainModel} from "../rankings/rankings.domain-model";
import {LineupsMemberSeasonStatsApiModel} from "./lineups-member/season-stats/lineups-member.season-stats.api-model";
import {PositionDomainModel} from "../position/position.domain-model";
import {FormationPositionDomainModel} from "../formation/formation-position.domain-model";
import {LineupsMemberYardFormationDomainModel} from "./lineups-member/yard-formation/lineups-member-yard-formation-domain-model";
import {LineupsMemberInjuryDomainModel} from "./lineups-member/injury/lineups-member-injury-domain-model";
import {LineupsMemberSuspensionDomainModel} from "./lineups-member/suspension/lineups-member-suspension-domain-model";
import {LineupsMemberSeasonStatsDomainModel} from "./lineups-member/season-stats/lineups-member.season-stats.domain-model";
import {PositionApiModel} from "../position/position.api-model";
import {FormationPositionApiModel} from "../formation/formation-position.api-model";
import {LineupsMemberInjuryApiModel} from "./lineups-member/injury/lineups-member-injury-api-model";
import {LineupsMemberSuspensionApiModel} from "./lineups-member/suspension/lineups-member-suspension-api-model";
import {LineupsMemberYardFormationApiModel} from "./lineups-member/yard-formation/lineups-member-yard-formation-api-model";
import {CompetitorDomainModel} from "../../common/competitor/models/competitor-domain-model";
import {COMPETITOR_TYPES} from "../../../core/app/consts";

export class LineupFactory extends BaseFactory {
  memberFactory: MemberFactory

  static injectables = [...BaseFactory.injectables, MemberFactory];

  constructor(logger: LoggerUtils, terms: TermsUtils, memberFactory: MemberFactory) {
    super(logger, terms);

    contractsUtils.requiredType(memberFactory, MemberFactory);

    this.memberFactory = memberFactory;
  }

  createDomainModel(apiModel: LineupsApiModel, isNational: Boolean, athletes: Array<MemberApiModel>, sport: SportDomainModel): LineupsDomainModel {
    if (!apiModel || !athletes) {
      return null;
    }

    const athletesMap: Map<Number, MemberDomainModel> = this.memberFactory.createDomainMap(athletes, MemberDomainModel, 'id', sport)

    const lineupsMembers: Array<LineupsMemberDomainModel> = apiModel.members?.map((member: LineupsMemberApiModel) => {
      const athleteApiModel: MemberApiModel = athletesMap.get(member.id);

      const domain: LineupsMemberDomainModel = this.createLineupsMemberDomainObject(athleteApiModel, member, isNational, sport);
      return domain;
    });

    const domain: LineupsDomainModel = new LineupsDomainModel(apiModel, lineupsMembers);

    return domain;
  }

  createRankingsDomainObject(rankingsApiModel: Array<RankingsApiModel>): RankingsDomainModel {
    const rankings: Array<RankingsDomainModel> = rankingsApiModel ? rankingsApiModel?.map((ranking: RankingsApiModel) => new RankingsDomainModel(ranking)) : null

    return rankings;
  }

  createLineupsMemberDomainObject(athleteDomainModel: MemberDomainModel, apiModel: LineupsMemberApiModel, isNational: Boolean, sport: SportDomainModel): LineupsMemberDomainModel {
    const position: PositionDomainModel = apiModel.position ? this.createPositionDomainObject(apiModel.position) : null;
    const formation: FormationPositionDomainModel = apiModel.formation ? this.createFormationDomainObject(apiModel.formation) : null;
    const yardFormation: LineupsMemberYardFormationDomainModel = apiModel.yardFormation ? this.createYardFormationDomainObject(apiModel.yardFormation) : null;
    const injury: LineupsMemberInjuryDomainModel = apiModel.injury ? this.createInjuryDomainObject(apiModel.injury) : null;
    const suspension: LineupsMemberSuspensionDomainModel = apiModel.suspension ? this.createSuspensionDomainObject(apiModel.suspension) : null;
    const seasonStats: LineupsMemberSeasonStatsDomainModel = apiModel.seasonStats?.map((stat: LineupsMemberSeasonStatsApiModel) => new LineupsMemberSeasonStatsDomainModel(stat));
    const competitorNum: Number = apiModel.competitorNum > 0 ? apiModel.competitorNum: null;

    const domain: LineupsMemberDomainModel = new LineupsMemberDomainModel(athleteDomainModel.apiModel, apiModel, isNational, position, formation, yardFormation, injury, suspension, seasonStats, [], [], sport, competitorNum);

    return domain;
  }

  createLineupMemberYardDomainModel(apiModel: LineupsMemberApiModel, sport: SportDomainModel, competitor: CompetitorDomainModel, isNational: Boolean){
    const position: PositionDomainModel = apiModel.position ? this.createPositionDomainObject(apiModel.position) : null;
    const formation: FormationPositionDomainModel = apiModel.formation ? this.createFormationDomainObject(apiModel.formation) : null;
    const yardFormation: LineupsMemberYardFormationDomainModel = apiModel.yardFormation ? this.createYardFormationDomainObject(apiModel.yardFormation) : null;

    const domain: LineupsMemberDomainModel = new LineupsMemberDomainModel(apiModel, apiModel, isNational, position, formation, yardFormation, null, null, null, [], [], sport, -1, competitor);
    return domain;
  }

  createLineupYardDomainModel(apiModel: LineupsApiModel, sport: SportDomainModel, competitors: Map<Number, CompetitorDomainModel>, key: String): LineupsDomainModel {
    const lineupsMembers: Array<LineupsMemberDomainModel> = apiModel.members?.map((member: LineupsMemberApiModel) => {
      const competitor: CompetitorDomainModel = competitors.get(member.competitorId);
      const isNational = competitor.type === COMPETITOR_TYPES.nationalTeam;
      const domain: LineupsMemberDomainModel = this.createLineupMemberYardDomainModel(member, sport, competitor, isNational);

      return domain;
    });

    const domain: LineupsDomainModel = new LineupsDomainModel(apiModel, lineupsMembers, key);

    return domain;
  }

  createPositionDomainObject(position: PositionApiModel): PositionDomainModel {
    const domain: PositionDomainModel = new PositionDomainModel(position);

    return domain;
  }

  createFormationDomainObject(formation: FormationPositionApiModel): FormationPositionDomainModel {
    const domain: FormationPositionDomainModel = new FormationPositionDomainModel(formation);

    return domain;
  }

  createInjuryDomainObject(injury: LineupsMemberInjuryApiModel): LineupsMemberInjuryDomainModel {
    const domain: LineupsMemberInjuryDomainModel = new LineupsMemberInjuryDomainModel(injury);

    return domain;
  }

  createSuspensionDomainObject(suspension: LineupsMemberSuspensionApiModel): LineupsMemberSuspensionDomainModel {
    const domain: LineupsMemberSuspensionDomainModel = new LineupsMemberSuspensionDomainModel(suspension);

    return domain;
  }

  createYardFormationDomainObject(yardFormation: LineupsMemberYardFormationApiModel): LineupsMemberYardFormationDomainModel {
    const domain: LineupsMemberYardFormationDomainModel = new LineupsMemberYardFormationDomainModel(yardFormation);

    return domain;
  }

}

